import Image from '@/src/components/Image';
import { Button } from '@/src/components/ui/button';
import { Link, useRouter } from '@/src/i18n/navigation';
import { AlertCircle, ArrowLeft, ArrowRight, ChevronRight } from 'lucide-react';
import React, { useTransition } from 'react';
import ProductCart from './ProductCart';
import { TCart } from '@/src/types/carts';
import { PRODUCT_IMAGE_SIZE } from '@/src/lib/utils/command';
import { useLocale, useTranslations } from 'next-intl';
import { Routes } from '@/src/routes/routes';
import useCartStore from '@/src/lib/state-stores/cartStore';
import CurrentLanguageText from '@/src/components/helper/CurrentLanguageText';
import PriceDisplay from '@/src/components/sarComponent';
import Typography from '@/src/components/ui/typography';
import { Alert, AlertDescription } from '@/src/components/ui/alert';
import MinimumOrderAmount from './MinimumOrderAmount';
import { trackEvent } from '@/src/lib/amplitude';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';

type TCartProps = {
  carts: TCart[];
  onCloseCartSheet?: () => void;
};

const StoreCart = ({ carts, onCloseCartSheet }: TCartProps) => {
  const { deleteCart, cartId, setCartId, setIsGiftCheckout } = useCartStore();

  const [isPending, startTransition] = useTransition();
  const router = useRouter();

  const lang = useLocale();
  const t = useTranslations('cart');

  const handleCloseSheet = ({ shop_id, shop_name }: { shop_name: string; shop_id: number }) => {
    if (onCloseCartSheet) {
      onCloseCartSheet();
    }
    trackEvent(AmplitudeEvents.store_clicked, {
      shop_id: shop_id,
      shop_name: shop_name,
    });
  };

  const goToCheckout = (id: string | number) => {
    onCloseCartSheet?.();
    trackEvent(AmplitudeEvents.checkout_started, {
      cart_id: Number(id),
      cart_items_count: carts.find((cart) => cart.id === id)?.cartItems.length || 0,
      product_IDs:
        carts.find((cart) => cart.id === id)?.cartItems.map((item) => Number(item.id)) || [],
    });

    setTimeout(() => {
      setIsGiftCheckout(false);
      router.push(Routes.checkout(id));
    }, 100);
  };

  const handleDeleteCart = (cartId: number | string) => {
    startTransition(() => {
      deleteCart(cartId);
    });
  };

  const goToChekoutGiftPage = (id: string | number) => {
    onCloseCartSheet?.();
    setIsGiftCheckout(true);
    trackEvent(AmplitudeEvents.gift_checkout_started, {
      cart_id: Number(id),
      cart_items_count: carts.find((cart) => cart.id === id)?.cartItems.length || 0,
      product_IDs:
        carts.find((cart) => cart.id === id)?.cartItems.map((item) => Number(item.id)) || [],
    });
    setTimeout(() => {
      router.push({
        pathname: Routes.checkout(id),
        query: {
          isGift: 'true',
        },
      });
    }, 60);
  };

  return (
    <>
      {cartId && (
        <button className="py-4 hover:opacity-70" onClick={() => setCartId('')}>
          <ArrowLeft className="rtl:rotate-180" />
        </button>
      )}

      {carts?.map((item, index) => (
        <div key={index} className="mb-3 flex flex-col gap-3 rounded-lg bg-white p-3 shadow-sm">
          {/* store details */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                href={
                  item.cartType === 3
                    ? Routes.digitalCards
                    : Routes.branchDetails(item.branch.store.id, item.branch.store.englishName)
                }
                onClick={() => {
                  handleCloseSheet({
                    shop_id: item.branch.store.id,
                    shop_name: item.branch.store.englishName,
                  });
                }}
                passHref
              >
                <Image
                  key={item.branch.store.logoUrl}
                  src={item.branch.store.logoUrl}
                  size={PRODUCT_IMAGE_SIZE.MEDIUM}
                  width={50}
                  height={50}
                  alt={item.branch.englishName}
                  className="h-14 w-14 rounded-lg border-2 border-slate-200 object-cover"
                />
              </Link>
              <div className="flex flex-col">
                <h3 className="text-lg font-bold">
                  <CurrentLanguageText
                    arabicName={item.branch.store.arabicName}
                    englishName={item.branch.store.englishName}
                  />
                </h3>
                <p className="text-gray-5 text-sm md:text-base">
                  <CurrentLanguageText
                    arabicName={item.branch.arabicName}
                    englishName={item.branch.englishName}
                  />
                </p>
              </div>
            </div>
            <Link
              href={
                item.cartType === 3
                  ? Routes.digitalCards
                  : Routes.branchDetails(item.branch.store.id, item.branch.store.englishName)
              }
              onClick={() =>
                handleCloseSheet({
                  shop_id: item.branch.store.id,
                  shop_name: item.branch.store.englishName,
                })
              }
              className="bg-primary flex h-7 w-7 items-center justify-center rounded-full text-white"
            >
              {lang === 'ar' ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
            </Link>
          </div>

          {/* gift button  */}
          {cartId && item?.branch?.isAllowGiftOrder && (
            <button
              className="bg-linear-gift my-2 flex w-full items-center justify-between gap-2 rounded-xl p-2 text-sm font-bold text-white"
              onClick={() => goToChekoutGiftPage(item.id)}
            >
              <span className="flex items-center gap-2">
                <Image src="/giftIcon.png" alt="gift" width={30} height={30} local />
                {t('sendThisOrderAsAGift')}
              </span>

              <ChevronRight className="rtl:rotate-180" />
            </button>
          )}

          {/* number of products at cart  */}
          <Typography>
            <span className="font-bold">
              {t('cart-item', { item: `${item.cartItems.length}` })}
            </span>
          </Typography>

          {/* error message  */}

          {item.checkoutErrorMessage && (
            <Alert variant={'warning'} className="rounded-xl py-1">
              <AlertDescription className="flex items-center gap-3 font-bold">
                <AlertCircle className="h-4 w-4" />
                {item.checkoutErrorMessage}
              </AlertDescription>
            </Alert>
          )}

          {/* list of products in cart */}
          {item.cartItems.map((cart) => (
            <ProductCart
              key={cart.id}
              cart={cart}
              branchId={item.branch.id}
              onCloseCartSheet={onCloseCartSheet}
              cardType={item.cartType}
              currencyCode={item.branch.city.currencyCode}
              countryId={item.branch.city.countryId}
              branchName={item.branch.englishName}
              storeName={item.branch.store.englishName}
              storeId={item.branch.store.id}
            />
          ))}

          {/* the progress logic  */}

          <MinimumOrderAmount
            countryId={item.branch.city.countryId}
            minimumOrderAmount={item.minimumOrderAmount}
            totalPrice={item.totalPrice}
          />

          {/* checkout and clear products from cart buttons */}

          <div className="flex w-full items-center justify-between gap-4 lg:gap-8">
            <button
              disabled={isPending}
              onClick={() => handleDeleteCart(item.id)}
              className="flex-1 basis-1/3 cursor-pointer border-0 text-sm font-bold underline outline-hidden"
            >
              {t('clear-cart')} {isPending && <span className="loader"></span>}
            </button>
            {item.canCheckout && cartId ? (
              <Button
                rounded={'full'}
                className={`flex basis-3/2 font-bold lg:text-base ltr:flex-row-reverse`}
                disabled={!item.canCheckout}
                onClick={() => goToCheckout(item.id)}
              >
                {t('checkout')}
                <PriceDisplay
                  price={Number(item.totalPrice?.toFixed(2))}
                  size="small"
                  currency={item.branch.city.currencyCode}
                  countryId={item.branch.city.countryId}
                  className="mx-1 font-semibold"
                />
              </Button>
            ) : (
              <Button
                rounded={'full'}
                className={`flex basis-3/2 font-bold lg:text-base ltr:flex-row-reverse`}
                disabled={!item.canCheckout}
                onClick={() => setCartId(String(item.id))}
              >
                {t('ViewCart')}
              </Button>
            )}
          </div>
        </div>
      ))}
    </>
  );
};

export default StoreCart;
